<template>
  <div class="agent-performance-view">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <router-link to="/agent-dashboard" class="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </router-link>
        <div>
          <h1>My Performance</h1>
          <p class="subtitle">Track your achievements and metrics</p>
        </div>
      </div>
      <button class="refresh-button" @click="loadPerformanceData" :disabled="isLoading">
        <svg :class="{ spinning: isLoading }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
        </svg>
        Refresh
      </button>
    </div>

    <!-- Overview Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon satisfaction">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
            </svg>
          </div>
          <span class="stat-label">Customer Satisfaction</span>
        </div>
        <div class="stat-value">{{ performance.satisfaction }}%</div>
        <div class="stat-change positive">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14l5-5 5 5z"/>
          </svg>
          +5% from last month
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon handle-time">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
            </svg>
          </div>
          <span class="stat-label">Avg Handle Time</span>
        </div>
        <div class="stat-value">{{ performance.avgHandleTime }} min</div>
        <div class="stat-change negative">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 10l5 5 5-5z"/>
          </svg>
          +2 min from last month
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon conversion">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <span class="stat-label">Leads Converted</span>
        </div>
        <div class="stat-value">{{ performance.leadsConverted }}</div>
        <div class="stat-change positive">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14l5-5 5 5z"/>
          </svg>
          +12 from last month
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon messages">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
            </svg>
          </div>
          <span class="stat-label">Messages Sent</span>
        </div>
        <div class="stat-value">{{ performance.messagesSent }}</div>
        <div class="stat-change positive">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14l5-5 5 5z"/>
          </svg>
          +84 from last month
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="charts-section">
      <!-- Performance Trend -->
      <div class="chart-card">
        <h2>Performance Trend</h2>
        <div class="chart-placeholder">
          <svg width="100%" height="300" viewBox="0 0 600 300">
            <!-- Grid lines -->
            <line x1="50" y1="250" x2="550" y2="250" stroke="#e2e8f0" stroke-width="1"/>
            <line x1="50" y1="200" x2="550" y2="200" stroke="#e2e8f0" stroke-width="1"/>
            <line x1="50" y1="150" x2="550" y2="150" stroke="#e2e8f0" stroke-width="1"/>
            <line x1="50" y1="100" x2="550" y2="100" stroke="#e2e8f0" stroke-width="1"/>
            <line x1="50" y1="50" x2="550" y2="50" stroke="#e2e8f0" stroke-width="1"/>

            <!-- Performance line -->
            <polyline
              points="50,200 150,180 250,150 350,140 450,120 550,100"
              fill="none"
              stroke="url(#gradient)"
              stroke-width="3"
              stroke-linecap="round"
            />

            <!-- Data points -->
            <circle cx="50" cy="200" r="4" fill="#667eea"/>
            <circle cx="150" cy="180" r="4" fill="#667eea"/>
            <circle cx="250" cy="150" r="4" fill="#667eea"/>
            <circle cx="350" cy="140" r="4" fill="#667eea"/>
            <circle cx="450" cy="120" r="4" fill="#667eea"/>
            <circle cx="550" cy="100" r="4" fill="#667eea"/>

            <!-- Labels -->
            <text x="50" y="275" text-anchor="middle" fill="#718096" font-size="12">Jan</text>
            <text x="150" y="275" text-anchor="middle" fill="#718096" font-size="12">Feb</text>
            <text x="250" y="275" text-anchor="middle" fill="#718096" font-size="12">Mar</text>
            <text x="350" y="275" text-anchor="middle" fill="#718096" font-size="12">Apr</text>
            <text x="450" y="275" text-anchor="middle" fill="#718096" font-size="12">May</text>
            <text x="550" y="275" text-anchor="middle" fill="#718096" font-size="12">Jun</text>

            <!-- Gradient definition -->
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      <!-- Activity Breakdown -->
      <div class="chart-card">
        <h2>Activity Breakdown</h2>
        <div class="activity-list">
          <div class="activity-item">
            <div class="activity-bar-container">
              <div class="activity-label">Calls Made</div>
              <div class="activity-bar">
                <div class="activity-fill" :style="{ width: '85%' }"></div>
              </div>
            </div>
            <span class="activity-value">342</span>
          </div>

          <div class="activity-item">
            <div class="activity-bar-container">
              <div class="activity-label">Emails Sent</div>
              <div class="activity-bar">
                <div class="activity-fill" :style="{ width: '65%' }"></div>
              </div>
            </div>
            <span class="activity-value">210</span>
          </div>

          <div class="activity-item">
            <div class="activity-bar-container">
              <div class="activity-label">WhatsApp Messages</div>
              <div class="activity-bar">
                <div class="activity-fill" :style="{ width: '92%' }"></div>
              </div>
            </div>
            <span class="activity-value">456</span>
          </div>

          <div class="activity-item">
            <div class="activity-bar-container">
              <div class="activity-label">Meetings Scheduled</div>
              <div class="activity-bar">
                <div class="activity-fill" :style="{ width: '45%' }"></div>
              </div>
            </div>
            <span class="activity-value">78</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Achievements -->
    <div class="achievements-section">
      <h2>Recent Achievements</h2>
      <div class="achievements-grid">
        <div class="achievement-card">
          <div class="achievement-icon gold">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <h3>Top Performer</h3>
          <p>Highest conversion rate this month</p>
        </div>

        <div class="achievement-card">
          <div class="achievement-icon silver">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
            </svg>
          </div>
          <h3>Quick Responder</h3>
          <p>Average response time under 5 minutes</p>
        </div>

        <div class="achievement-card">
          <div class="achievement-icon bronze">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3>Customer Favorite</h3>
          <p>95% customer satisfaction rating</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentPerformanceView',
  data () {
    return {
      performance: {
        satisfaction: 95,
        avgHandleTime: 12,
        leadsConverted: 48,
        messagesSent: 456
      },
      isLoading: false
    }
  },
  async mounted () {
    // Check authentication
    const agentToken = localStorage.getItem('agent_token')
    if (!agentToken) {
      this.$router.push('/agent-login')
      return
    }

    await this.loadPerformanceData()
  },
  methods: {
    async loadPerformanceData () {
      this.isLoading = true
      // const agentToken = localStorage.getItem('agent_token')

      try {
        // TODO: Load actual performance data from API
        // const agentToken = localStorage.getItem('agent_token')
        // const response = await fetch('https://api.quali-d.com/agents/my-performance', {
        //   headers: {
        //     Authorization: `Bearer ${agentToken}`,
        //     Accept: 'application/json'
        //   }
        // })

        await new Promise(resolve => setTimeout(resolve, 800))

        // Mock data - replace with actual API call
        this.performance = {
          satisfaction: 95,
          avgHandleTime: 12,
          leadsConverted: 48,
          messagesSent: 456
        }
      } catch (error) {
        console.error('Error loading performance data:', error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.agent-performance-view {
  min-height: 100vh;
  background: #f7fafc;
  padding: 24px;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  color: #4a5568;
}

.back-button:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 4px 0;
}

.subtitle {
  font-size: 14px;
  color: #718096;
  margin: 0;
}

.refresh-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-button:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.refresh-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.refresh-button svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-icon.satisfaction {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-icon.handle-time {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.stat-icon.conversion {
  background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);
}

.stat-icon.messages {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
}

.stat-label {
  font-size: 13px;
  color: #718096;
  font-weight: 600;
}

.stat-value {
  font-size: 36px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-change {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
}

.stat-change.positive {
  color: #059669;
}

.stat-change.negative {
  color: #dc2626;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-card h2 {
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 20px 0;
}

.chart-placeholder {
  width: 100%;
  overflow-x: auto;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 16px;
}

.activity-bar-container {
  flex: 1;
}

.activity-label {
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
}

.activity-bar {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.activity-fill {
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.6s ease;
}

.activity-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  min-width: 50px;
  text-align: right;
}

/* Achievements */
.achievements-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.achievements-section h2 {
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 20px 0;
}

.achievements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.achievement-card {
  padding: 24px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  text-align: center;
  transition: all 0.2s;
}

.achievement-card:hover {
  border-color: #cbd5e0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.achievement-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: white;
}

.achievement-icon.gold {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
}

.achievement-icon.silver {
  background: linear-gradient(135deg, #e0e7ff 0%, #cbd5e1 100%);
}

.achievement-icon.bronze {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
}

.achievement-card h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
}

.achievement-card p {
  font-size: 13px;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  .agent-performance-view {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .achievements-grid {
    grid-template-columns: 1fr;
  }
}
</style>
